/**
 * Dimension labels drawn on the map: edge lengths of the selected planning cell (nearly collinear
 * pieces merged, so rounded corners do not produce dozens of tiny numbers) and of the analysed
 * parcel (every edge), plus the area at the cell's pole of inaccessibility. Lengths and areas are
 * planar VN-2000 (KTT 105°, múi 3°), the same as the parcel tool.
 */
import { makePlanarProjector } from './core/vn2000.js';
import { polylabel } from './core/polylabel.js';
import { toPolygons, ringArea } from './core/geo.js';

const proj = makePlanarProjector('hn-105');
const nf1 = new Intl.NumberFormat('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nf2 = new Intl.NumberFormat('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf0 = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 });

const dir = (a, b) => Math.atan2(b[1] - a[1], b[0] - a[0]);
const angDiff = (a, b) => { let d = Math.abs(a - b) % (2 * Math.PI); return d > Math.PI ? 2 * Math.PI - d : d; };

/** Split a closed ring (lng/lat, open) into straight runs; returns [{ a: i0, b: i1, len }] in metres. */
function runs(ll, merge) {
  const n = ll.length;
  const xy = ll.map((p) => proj.forward(p[0], p[1]));
  const out = [];
  if (!merge) {
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      out.push({ pts: [ll[i], ll[j]], len: Math.hypot(xy[j][0] - xy[i][0], xy[j][1] - xy[i][1]) });
    }
    return out;
  }
  // start at the sharpest corner so a run never wraps across the ring start
  let start = 0, sharp = -1;
  for (let i = 0; i < n; i++) {
    const a = xy[(i - 1 + n) % n], b = xy[i], c = xy[(i + 1) % n];
    const t = angDiff(dir(a, b), dir(b, c));
    if (t > sharp) { sharp = t; start = i; }
  }
  const TOL = (9 * Math.PI) / 180;
  let i = 0;
  while (i < n) {
    const s = (start + i) % n;
    let e = (s + 1) % n, k = i + 1;
    const d0 = dir(xy[s], xy[e]);
    while (k < n) {
      const nx = (e + 1) % n;
      if (angDiff(dir(xy[e], xy[nx]), d0) > TOL || angDiff(dir(xy[s], xy[nx]), d0) > TOL / 2) break;
      e = nx; k++;
    }
    const pts = [];
    for (let q = s; ; q = (q + 1) % n) { pts.push(ll[q]); if (q === e) break; }
    out.push({ pts, len: Math.hypot(xy[e][0] - xy[s][0], xy[e][1] - xy[s][1]) });
    i = k;
  }
  return out;
}

function areaPoint(poly) {
  const xy = poly.map((r) => r.map((p) => proj.forward(p[0], p[1])));
  const a = ringArea(xy[0]) - xy.slice(1).reduce((s, h) => s + ringArea(h), 0);
  const [x, y] = polylabel(xy, 0.5);
  return { a, p: proj.inverse(x, y) };
}

const open = (r) => (r.length > 1 && r[0][0] === r[r.length - 1][0] && r[0][1] === r[r.length - 1][1] ? r.slice(0, -1) : r);

/**
 * @param {object|null} cellGeom  highlighted planning cell (Polygon / MultiPolygon)
 * @param {Array<Array<[number,number]>>|null} parcelRing analysed parcel ring (lng/lat)
 */
export function buildDims(cellGeom, parcelRing) {
  const features = [];
  if (cellGeom) {
    let total = 0, bestA = -1, bestP = null;
    for (const poly of toPolygons(cellGeom)) {
      const outer = open(poly[0]);
      if (outer.length < 3) continue;
      for (const r of runs(outer, true)) {
        if (r.len < 3) continue;
        features.push({ type: 'Feature', properties: { k: 'cell', t: (r.len >= 100 ? nf1 : nf2).format(r.len).replace(/,(\d)0$/, ',$1') + '\u00a0m', len: r.len }, geometry: { type: 'LineString', coordinates: r.pts } });
      }
      const ap = areaPoint(poly.map(open));
      total += ap.a;
      if (ap.a > bestA) { bestA = ap.a; bestP = ap.p; }
    }
    if (bestP) features.push({ type: 'Feature', properties: { k: 'cell-area', t: total >= 10000 ? nf2.format(total / 10000) + ' ha' : nf0.format(total) + ' m²' }, geometry: { type: 'Point', coordinates: bestP } });
  }
  if (parcelRing && parcelRing.length >= 3) {
    const ring = open(parcelRing);
    for (const r of runs(ring, false)) {
      if (r.len < 0.3) continue;
      features.push({ type: 'Feature', properties: { k: 'parcel', t: nf2.format(r.len) + '\u00a0m', len: r.len }, geometry: { type: 'LineString', coordinates: r.pts } });
    }
  }
  return { type: 'FeatureCollection', features };
}
